import { CloseOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
////////////////////
////////////////////
//import React, { useEffect, useMemo } from "react";
import {
  Box,
  Chip, //Button,
  //Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel, //Typography,
  IconButton,
  InputAdornment,
  Radio, //Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useAtom } from "jotai/index";
import { isArray, isString, isUndefined } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
//import { useAtom } from "jotai";
import { Controller, FieldError, useForm } from "react-hook-form";
import { CheckboxElement, TextFieldElement, useFieldArray } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { AAutocompleteElement, BaseAutocomplete } from "@/components/a-autocomplete";
//import { useTranslation } from "react-i18next";
//import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
//import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { NumberListInput } from "@/components/number-list-input/number-list-input";
import { isOwner } from "@/lib/auth-predicates";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
import { meStore } from "@/store/store";

import { aiType, room, straddle, technology } from "./constants";
import { EditRoute, EditedRoute } from "./types";

//import { isOwner } from "@/lib/auth-predicates";
//import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
//import { meStore } from "@/store/store";

// --- NOTE ---
// The following helpers/constants are expected to exist in your codebase (they were used in original):
// aiType, room, technology, straddle, sizeRange, playerCountRange, arrayNumberNormalizer, beforeSendNormalizers, dataFieldName, formInitState, required
// Keep using them as before to avoid breaking changes.

const BOT_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 9];

type Props = {
  open: boolean;
  initialData?: EditRoute;
  onSave?: (data: EditedRoute) => void;
  onCancel?: () => void;
  confirmLoading?: boolean;
  disabled?: boolean;
};

const RouterEditModal = ({
  open,
  initialData,
  onSave,
  onCancel,
  confirmLoading = false,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const { data: authGroupsRs, isLoading: authGroupsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const { data: clusteringPatternsRs } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.CLUSTERING_PATTERN,
  });

  const { data: aiProfilesRs } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.AI_PROFILE,
  });

  const { data: gameTypesRs } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.GAME_TYPE,
  });

  const authGroupsOptions = useMemo(
    () => authGroupsRs?.data.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [authGroupsRs?.data]
  );

  const groupPatternsOptions = useMemo(
    () =>
      clusteringPatternsRs?.data.content
        ?.filter((item: any) => item.enabled)
        .map((item: any) => item.name)
        .sort((a: string, b: string) => a.localeCompare(b)) ?? [],
    [clusteringPatternsRs]
  );

  const aiProfileOptions = useMemo(
    () =>
      aiProfilesRs?.data.content
        ?.filter((item: any) => item.enabled)
        .map((item: any) => item.name)
        .sort((a: string, b: string) => a.localeCompare(b)) ?? [],
    [aiProfilesRs]
  );

  const gameTypeOptions = useMemo(
    () =>
      gameTypesRs?.data.content
        ?.filter((item: any) => item.enabled)
        .map((item: any) => item.name)
        .sort((a: string, b: string) => a.localeCompare(b)) ?? [],
    [gameTypesRs]
  );

  // zod schema approximates the shape used in original AntD form:
  const schema = useMemo(() => {
    const instanceSchema = z.object({
      ai_type: z.string() /*.optional()*/, //only one list
      isAiTypeCustom: z.boolean().optional(),
      instance: z.string().min(1, t("fieldRequired")), //address
      probability: z.coerce.number().min(0, ">0").max(100, "⩽100"),
      always_active: z.boolean().default(false),
    });

    const dataSchema = z.object({
      priority: z.coerce.number().min(0),
      room: z.array(z.string()),
      technology: z.array(z.string()),
      only_bot: z.union([z.literal("true"), z.literal("false"), z.literal("")]), //onlybot is string but obliged to be "true", "false", ""
      partner_ai_key: z.array(z.string()), //emty array/array of any values
      ai_profile: z.array(z.string()), //values from list / or empty array
      game_type: z.array(z.string()), //values from list / or empty array
      size: z.array(z.coerce.number()),
      aid: z.array(z.string()), //emty/array of any values
      cid: z.array(z.string()), //emty/array of any values
      game_group: z.array(z.string()), //emty/array of any values
      ai_type: z.string().optional(), //only from list or absent
      limit: z.array(z.number()),
      straddle: z.array(z.coerce.number()), //only numbers, or emty array
      ante: z.array(z.coerce.number()), //only numbers, or emty array
      players_count: z.array(
        z
          .number()
          .min(2, "Must be bigger than 2")
          .max(9, "Myst be less then 9")
          .int("Must be integer")
      ), //array from 2 to 9or empty array
      comment: z.string().nullish(),
      group_pattern: z.array(z.string()).optional(), //only from values or empty array
      single_instance: z.boolean().default(false), //optional().
      instances: z
        .array(instanceSchema)
        .optional()
        .default([])
        .superRefine(
          (val, ctx) => {
            // instances probability sum check
            const instances = val ?? [];
            if (!instances || instances.length === 0) return true;
            const sum = instances.reduce((s, it) => s + (Number(it.probability) || 0), 0);

            if (sum !== 100) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("sumProbabilityMustBeEqual100"),
                path: [],
              });
            }
          } /*, { message: t("sumProbabilityMustBeEqual100"), path: ["data", "instances"] }*/
        ),
      bots_count: z.array(z.number()),
      game_group_bots_count: z.array(z.number()),
    });

    return z
      .object({
        name: z.string().optional(),
        authGroups: isOwner(me)
          ? z.array(z.string())
          : z.array(z.string()).min(1, t("mustContainsGroup")),
        data: dataSchema,
      })
      .transform((val) => ({
        ...val,
        name: String(val.data.priority ?? ""),
      }));
    /*.superRefine((val, ctx) => {
        // instances probability sum check
        const instances = val.data?.instances ?? [];
        if (!instances || instances.length === 0) return true;
        const sum = instances.reduce((s, it) => s + (Number(it.probability) || 0), 0);
        //return sum === 100;
        if (sum > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("sumProbabilityMustBeEqual100"),
            path: ["data", "instances"],
          });
        }
      }/!*, { message: t("sumProbabilityMustBeEqual100"), path: ["data", "instances"] }*!/);*/
  }, [me, t]);

  type FormSchema = z.infer<typeof schema>;

  // compute default values similar to original
  const defaultValues = useMemo<FormSchema>(() => {
    let authGroups = initialData?.auth_groups ? Array.from(initialData.auth_groups) : [];
    if (authGroupsRs?.data?.length === 1 && !isOwner(me)) {
      authGroups = authGroupsRs.data.map((it: any) => it.name);
    }

    const data = (initialData?.data ?? {}) as any;

    return {
      name: initialData?.name ?? "",
      authGroups: authGroups,
      data: {
        priority: data.priority ?? undefined,
        room: data.room ?? [],
        technology: data.technology ?? [],
        only_bot: data.only_bot ?? "",
        partner_ai_key: data.partner_ai_key ?? [],
        ai_profile: data.ai_profile ?? [],
        game_type: data.game_type ?? [],
        size: data.size ?? [],
        aid: data.aid ?? [],
        cid: data.cid ?? [],
        game_group: data.game_group ?? [],
        ai_type: data.ai_type ?? "",
        limit: data.limit ?? [],
        straddle: data.straddle ?? [],
        ante: data.ante ?? [],
        players_count: data.players_count ?? [],
        comment: data.comment ?? "",
        group_pattern: data.group_pattern ?? [],
        single_instance: data.single_instance ?? false,
        instances: Array.isArray(data.instances)
          ? data.instances.map((it: any) => ({
              isAiTypeCustom: !aiType.map((item) => item.value).includes(it.ai_type),
              ai_type: it.ai_type,
              instance: it.instance,
              probability: it.probability,
              always_active: !!it.always_active,
            }))
          : [],
        bots_count: data.bots_count ?? [],
        game_group_bots_count: data.game_group_bots_count ?? [],
      },
    };
  }, [initialData, authGroupsRs, me]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
    getValues,
    trigger,
    watch,
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onChange",
  });

  watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, open]);

  const {
    fields: instanceFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "data.instances",
  });

  const submit = handleSubmit((values) => {
    // apply beforeSendNormalizers if present in your project (keeps parity with original)
    const result: any = {
      ...values,
      auth_groups: new Set(values.authGroups ?? []),
      data: { ...values.data },
    };

    /*if (typeof beforeSendNormalizers !== "undefined") {
      (beforeSendNormalizers as any[]).forEach((normalizer) => {
        result = normalizer(result);
      });
    }*/

    if (onSave) onSave(result as EditedRoute);
  });

  const validateNumberInputList = useCallback((val: number) => {
    return !(val === 0 || val > 9);
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return; //ask question if changes==true and click outSide
        onCancel?.();
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ pt: 1 }}>
          {!defaultValues.name ? t("createNewRouterRule") : t("editRouterRule")}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {/* name */}
          {/*<TextFieldElement
            control={control}
            name="name"
            label={t("name")}
            error={!!errors.name?.message}
            helperText={errors.name?.message}
            hidden
          />*/}

          {/* authGroups */}
          <AAutocompleteElement
            name="authGroups"
            control={control}
            label={t("authGroups")}
            required
            multiple
            loading={authGroupsLoading}
            options={authGroupsOptions.map((it) => it.name)}
            textFieldProps={{
              variant: "outlined",
              error: !!errors.authGroups?.message,
              helperText: errors.authGroups?.message,
            }}
          />

          {/* priority */}
          <TextFieldElement
            control={control}
            variant="outlined"
            name="data.priority"
            label={t("priority")}
            type="number"
            error={!!errors.data?.priority?.message}
            helperText={errors.data?.priority?.message}
            hidden
            InputProps={{
              inputProps: { min: 1 },
            }}
          />

          {/* rooms */}
          <Controller
            name="data.room"
            control={control}
            render={({ field }) => (
              <BaseAutocomplete
                {...field}
                multiple
                freeSolo
                options={(room ?? []).map((r) => r.label)}
                value={field.value ?? []}
                onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("rooms")}
                    placeholder={t("rooms")}
                    fullWidth
                    error={!!errors.data?.room?.message}
                    helperText={errors.data?.room?.message}
                  />
                )}
              />
            )}
          />

          {/* technologies */}
          <Controller
            name="data.technology"
            control={control}
            render={({ field }) => (
              <BaseAutocomplete
                {...field}
                multiple
                options={(technology ?? []).map((t) => t.label)}
                value={field.value ?? []}
                onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("technologies")}
                    placeholder={t("technologies")}
                    fullWidth
                    error={!!errors.data?.technology?.message}
                    helperText={errors.data?.technology?.message}
                  />
                )}
              />
            )}
          />

          {/* only_bot */}
          <Controller
            name="data.only_bot"
            control={control}
            render={({ field }) => (
              <FormControl>
                <Typography variant="subtitle2">{t("onlyBot")}</Typography>
                <RadioGroup
                  row
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <FormControlLabel value="" control={<Radio />} label={t("none")} />
                  <FormControlLabel value="true" control={<Radio />} label={t("true")} />
                  <FormControlLabel value="false" control={<Radio />} label={t("false")} />
                </RadioGroup>
              </FormControl>
            )}
          />

          {/* partner_ai_key */}
          <Controller
            name="data.partner_ai_key"
            control={control}
            render={({ field }) => (
              <BaseAutocomplete
                {...field}
                multiple
                freeSolo
                options={[]}
                value={field.value ?? []}
                onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("partnerAIKeys")}
                    placeholder={t("partnerAIKeys")}
                    fullWidth
                    error={!!errors.data?.partner_ai_key?.message}
                    helperText={errors.data?.partner_ai_key?.message}
                  />
                )}
              />
            )}
          />

          {/* ai_profile */}
          <AAutocompleteElement
            name="data.ai_profile"
            control={control}
            label={t("aiProfiles")}
            multiple
            options={aiProfileOptions}
            textFieldProps={{
              variant: "outlined",
              error: !!errors.data?.ai_profile?.message,
              helperText: errors.data?.ai_profile?.message,
            }}
          />

          {/* game_type */}
          <AAutocompleteElement
            name="data.game_type"
            control={control}
            label={t("gameTypes")}
            multiple
            options={gameTypeOptions}
            textFieldProps={{
              variant: "outlined",
              error: !!errors.data?.game_type?.message,
              helperText: errors.data?.game_type?.message,
            }}
          />

          {/* size */}
          <Controller
            name="data.size"
            control={control}
            render={({ field }) => (
              <BaseAutocomplete
                {...field}
                multiple
                freeSolo
                value={field.value ?? []}
                options={[]}
                onChange={(_, v) =>
                  field.onChange(v.map((it: any) => (isNaN(Number(it)) ? it : Number(it))))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    type="number"
                    label={t("sizes")}
                    placeholder={t("from2to9")}
                    fullWidth
                    error={!!errors.data?.size?.message}
                    //helperText={errors.data?.size?.find(el => !!el?.message).message}
                    helperText={
                      Array.isArray(errors.data?.size) &&
                      errors
                        .data!.size!.filter(
                          (x): x is { message: string } =>
                            !!x && typeof x === "object" && "message" in x
                        )
                        .map((x, i) => (
                          <Typography key={i} color="error">
                            {x.message}
                          </Typography>
                        ))
                    }
                  />
                )}
              />
            )}
          />

          {/* Остальные поля (aid, cid, game_group) */}
          <Stack spacing={1}>
            {/*{["aid", "cid", "game_group"].map((fieldName) => (
              <Controller
                key={fieldName}
                name={`data.${fieldName}` as const}
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value ?? []}
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t(fieldName + "s")}
                        placeholder={t(fieldName + "s")}
                        fullWidth
                        error={!!errors.data?.[fieldName]?.message}
                        helperText={errors.data?.[fieldName]?.message}
                      />
                    )}
                  />
                )}
              />
            ))}*/}
            <Controller
              name="data.aid"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  options={[]}
                  multiple
                  freeSolo
                  value={field.value ?? []}
                  onChange={(_, v) => {
                    field.onChange(v);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("aids")}
                      placeholder={t("aids")}
                      error={!!errors.data?.aid?.message}
                      helperText={errors.data?.aid?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="data.cid"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  options={[]}
                  multiple
                  freeSolo
                  value={field.value ?? []}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("cids")}
                      placeholder={t("cids")}
                      error={!!errors.data?.cid?.message}
                      helperText={errors.data?.cid?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="data.game_group"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  options={[]}
                  multiple
                  freeSolo
                  value={field.value ?? []}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("gameGroups")}
                      placeholder={t("gameGroups")}
                      error={!!errors.data?.game_group?.message}
                      helperText={errors.data?.game_group?.message}
                    />
                  )}
                />
              )}
            />

            {/* ai_type */}
            <AAutocompleteElement
              control={control}
              name="data.ai_type"
              label={t("aiType")}
              options={(aiType ?? []).map((a) => a.label)}
              textFieldProps={{
                variant: "outlined",
                error: !!errors.data?.ai_type?.message,
                helperText: errors.data?.ai_type?.message,
              }}
            />
            {/*<Controller
              name="data.ai_type"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  //freeSolo
                  options={(aiType ?? []).map((a) => a.label)}
                  value={field.value ?? undefined}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("aiType")}
                      placeholder={t("aiTypes")}
                      fullWidth
                      error={!!errors.data?.ai_type?.message}
                      helperText={errors.data?.ai_type?.message}
                    />
                  )}
                />
              )}
            />*/}

            <Stack spacing={1}>
              <Controller
                name="data.limit"
                control={control}
                rules={{
                  required: t("fieldRequired"),
                  validate: (v) =>
                    v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t("enterOnlyNumber"),
                  },
                }}
                render={({
                  field: { onChange, onBlur, value, ref },
                  fieldState: { error },
                }) => (
                  <BaseAutocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={value}
                    disabled={disabled}
                    onChange={(_event, newValue) => {
                      onChange(newValue);

                      setValue(
                        "data.limit",
                        newValue.map((v) => parseInt(v.toString()))
                      ); // Update the value in react-hook-form
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        type="number"
                        variant="outlined"
                        label={t("limits")}
                        placeholder={t("addTag")}
                        inputRef={ref}
                        onBlur={onBlur} // Ensure onBlur is called to update the form state
                        error={!!error}
                        helperText={
                          (error as FieldError[] | undefined)?.filter(
                            (el) => !!el?.message
                          )?.[0]?.message
                        }
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip label={option} {...getTagProps({ index })} key={index} />
                      ))
                    }
                  />
                )}
              />

              <Controller
                name="data.straddle"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    multiple
                    options={(typeof straddle !== "undefined" ? straddle : []).map(
                      (s: any) => s.label || s
                    )}
                    value={field.value ?? []}
                    onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("straddles")}
                        placeholder={t("straddles")}
                        error={!!errors.data?.straddle?.message}
                        helperText={errors.data?.straddle?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="data.ante"
                control={control}
                rules={{
                  required: t("fieldRequired"),
                  validate: (v) =>
                    v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                  pattern: {
                    value: /^[0-9.]+$/,
                    message: t("enterOnlyNumber"),
                  },
                }}
                render={({
                  field: { onChange, onBlur, value, ref },
                  fieldState: { error },
                }) => (
                  <BaseAutocomplete //AutocompleteElement doesnt work with renderTags
                    multiple
                    freeSolo
                    options={[]}
                    value={value}
                    disabled={disabled}
                    onChange={(_event, newValue) => {
                      onChange(newValue);
                      setValue(
                        "data.ante",
                        newValue.map((v) => parseFloat(v.toString()))
                      ); // Update the value in react-hook-form
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        type="number"
                        variant="outlined"
                        label={t("ante")}
                        placeholder={t("addTag")}
                        inputRef={ref}
                        onBlur={onBlur} // Ensure onBlur is called to update the form state
                        error={!!error}
                        helperText={
                          (error as FieldError[] | undefined)?.filter(
                            (el) => !!el?.message
                          )?.[0]?.message
                        }
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip label={option} {...getTagProps({ index })} key={index} />
                      ))
                    }
                  />
                )}
              />
            </Stack>

            {/* players_count */}
            <Controller
              name="data.players_count"
              control={control}
              rules={{
                required: t("fieldRequired"),
                validate: (v) =>
                  v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                pattern: {
                  value: /[2-9]/,
                  message: "aaa",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <BaseAutocomplete
                  {...field}
                  options={[]}
                  multiple
                  freeSolo
                  value={field.value ?? []}
                  onChange={(_event, newValue) => {
                    field.onChange(newValue);

                    setValue(
                      "data.players_count",
                      newValue.map((v) => parseInt(v.toString()))
                    ); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="number"
                      label={t("playersCount")}
                      placeholder={t("from2to9")}
                      fullWidth
                      onBlur={field.onBlur}
                      error={!!error}
                      helperText={
                        (error as FieldError[] | undefined)?.filter((el) => !!el?.message)?.[0]
                          ?.message
                      }
                    />
                  )}
                />
              )}
            />

            {/* comment */}
            <TextFieldElement
              control={control}
              name="data.comment"
              label={t("comments")}
              error={!!errors.data?.comment?.message}
              helperText={errors.data?.comment?.message}
            />

            {/* group_pattern */}
            <AAutocompleteElement
              name="data.group_pattern"
              control={control}
              multiple
              options={groupPatternsOptions ?? []}
              label={t("groupPatterns")}
              textFieldProps={{
                variant: "outlined",
                error: !!errors.data?.group_pattern?.message,
                helperText: errors.data?.group_pattern?.message,
              }}
            />
          </Stack>

          {/* single_instance */}
          <Controller
            name="data.single_instance"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t("singleInstance")}
              />
            )}
          />

          {/* instances list */}
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">{t("instances")}</Typography>
              <IconButton
                size="small"
                onClick={() =>
                  append({
                    ai_type: aiType?.[0]?.value ?? "",
                    instance: "",
                    probability: 0,
                    always_active: false,
                  })
                }
                disabled={disabled}
              >
                <AddIcon />
              </IconButton>
            </Stack>

            {instanceFields.map((it, idx) => (
              <Box key={it.id}>
                <TextFieldElement
                  control={control}
                  name={`data.instances.${idx}.instance` as const}
                  label={t("address")}
                  error={!!errors.data?.instances?.[idx]?.instance?.message}
                  helperText={errors.data?.instances?.[idx]?.instance?.message}
                  size="small"
                  sx={{ flex: 1, marginBottom: "8px", width: "90%" }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Controller
                    name={`data.instances.${idx}.isAiTypeCustom` as const}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              setValue(`data.instances.${idx}.ai_type`, "");
                              trigger(`data.instances.${idx}.ai_type`);
                            }}
                          />
                        }
                        label={t("custom")}
                      />
                    )}
                  />

                  <Controller
                    name={`data.instances.${idx}.ai_type` as const}
                    control={control}
                    render={({ field, formState }) => {
                      const formValues = getValues();
                      const isAiTypeCustom = formValues.data.instances[idx].isAiTypeCustom;
                      return isAiTypeCustom ? (
                        <TextField
                          {...field}
                          size="small"
                          error={!!errors.data?.instances?.[idx]?.ai_type?.message}
                          helperText={errors.data?.instances?.[idx]?.ai_type?.message}
                          sx={{ minWidth: "150px", width: "150px" }}
                        />
                      ) : (
                        <BaseAutocomplete
                          {...field}
                          options={(aiType ?? []).map((a) => a.label)}
                          size="small"
                          sx={{ minWidth: 150 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              error={!!errors.data?.instances?.[idx]?.ai_type?.message}
                              helperText={errors.data?.instances?.[idx]?.ai_type?.message}
                            />
                          )}
                          onChange={(_, value) => field.onChange(value)}
                          disableClearable
                        />
                      );
                    }}
                  />

                  <TextFieldElement
                    control={control}
                    name={`data.instances.${idx}.probability` as const}
                    label={t("probability")}
                    type="number"
                    /*InputProps={ { endAdornment: <InputAdornment position="end">%</InputAdornment> }}*/
                    /*error={!!errors.data?.instances?.[idx]?.probability?.message}
                  helperText={errors.data?.instances?.[idx]?.probability?.message}*/
                    //parseError={() => null}
                    size="small"
                    sx={{ width: 100 }}
                  />

                  <Controller
                    name={`data.instances.${idx}.always_active` as const}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={t("alwaysActive")}
                      />
                    )}
                  />

                  <IconButton
                    aria-label="delete"
                    onClick={() => remove(idx)}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Box>
            ))}

            <Controller
              name="data.bots_count"
              control={control}
              render={({ field }) => (
                <NumberListInput
                  label={t("routerRulesTable.botsCount")}
                  value={field.value}
                  onChange={field.onChange}
                  parse={validateNumberInputList}
                  options={BOT_OPTIONS}
                />
              )}
            />

            <Controller
              name="data.game_group_bots_count"
              control={control}
              render={({ field }) => (
                <NumberListInput
                  label={t("routerRulesTable.gameGroupBotsCount")}
                  value={field.value}
                  onChange={field.onChange}
                  parse={validateNumberInputList}
                  options={BOT_OPTIONS}
                />
              )}
            />

            {errors.data?.instances?.root && (
              <Typography color="error">{errors.data.instances?.root?.message}</Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <ModalActionsButtons
          onCancelClick={() => onCancel && onCancel()}
          onConfirmClick={submit}
          disabled={disabled || !isDirty}
          loading={confirmLoading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RouterEditModal;
